import { CheckOutlined, CloseOutlined, RemoveOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { QuestionParts, TrialAnswerPartsIds } from "pages/Testing/view/Personal/types";

interface MultipleChoiceAnswerProps {
  questionParts: QuestionParts;
  partsIds: TrialAnswerPartsIds;
}

enum MultiChoiceAnswerStatus {
  correctNotChecked = "correctNotChecked",
  correctChecked = "correctChecked",
  incorrectChecked = "incorrectChecked",
  neutral = "neutral",
}

const getAnswerStatus = (checked: boolean, isCorrect: boolean) => {
  if (!checked && isCorrect) return MultiChoiceAnswerStatus.correctNotChecked;
  if (checked && isCorrect) return MultiChoiceAnswerStatus.correctChecked;
  if (checked && !isCorrect) return MultiChoiceAnswerStatus.incorrectChecked;

  return MultiChoiceAnswerStatus.neutral;
};

const getAnswerIcon = (answerStatus: MultiChoiceAnswerStatus) => {
  switch (answerStatus) {
    case MultiChoiceAnswerStatus.correctNotChecked:
      return (
        <Box sx={{ border: 1, borderColor: "green.contrast" }}>
          <RemoveOutlined color='success' />
        </Box>
      );
    case MultiChoiceAnswerStatus.correctChecked:
      return (
        <Box sx={{ backgroundColor: "green.contrast" }}>
          <CheckOutlined sx={{ color: "bgCase" }} />
        </Box>
      );
    case MultiChoiceAnswerStatus.incorrectChecked:
      return (
        <Box sx={{ backgroundColor: "red.contrast" }}>
          <CloseOutlined sx={{ color: "bgCase" }} />
        </Box>
      );
    default:
      return <Box sx={{ border: 1 }} />;
  }
};

const getAnswerTextColor = (answerStatus: MultiChoiceAnswerStatus) => {
  switch (answerStatus) {
    case MultiChoiceAnswerStatus.correctChecked:
      return "green.contrast";
    case MultiChoiceAnswerStatus.incorrectChecked:
      return "red.contrast";
    case MultiChoiceAnswerStatus.correctNotChecked:
    default:
      return "base.700";
  }
};

const MultipleChoiceAnswer = ({ questionParts, partsIds }: MultipleChoiceAnswerProps) => {
  return (
    <Stack spacing={1}>
      {questionParts?.map((questionPart) => {
        const checked = partsIds?.some((id) => questionPart.id === id) || false;
        const answerStatus = getAnswerStatus(checked, questionPart.isCorrect);
        const answerTextColor = getAnswerTextColor(answerStatus);

        return (
          <Stack
            key={questionPart.id}
            direction='row'
            alignItems='center'
            spacing={1}>
            <Box
              sx={{
                "& .MuiBox-root": {
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                },
                "& svg": { maxWidth: "100%", maxHeight: "100%" },
              }}>
              {getAnswerIcon(answerStatus)}
            </Box>
            <Typography color={answerTextColor}>{questionPart.content}</Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default MultipleChoiceAnswer;
